import { wikiPageCollection } from "./wiki-page.collection";
import type { DbClient } from "@tanstack/db";
import type { QueryClient } from "@tanstack/query-core";
import { apiPost, apiPut } from "@softmax/observatory-client/client";
import type {
  Schemas,
  WikiEditPublic,
} from "@softmax/observatory-client/types";
import {
  wikiIndexCollection,
  wikiRevisionsCollection,
  type WikiIdentity,
} from "./wiki.collections";

async function publishWikiRevision({
  db,
  edit,
  ...identity
}: WikiIdentity & { db: DbClient; edit: WikiEditPublic }) {
  const { wikiSlug, pageSlug, elevatedPrivileges } = identity;
  const signal = db.requireDependency<AbortController>("lifetime").signal;
  signal.throwIfAborted();
  const queryClient = db.requireDependency<QueryClient>("queryClient");
  await Promise.all([
    queryClient.cancelQueries({ queryKey: ["wikis", wikiSlug, "index"] }),
    queryClient.cancelQueries({
      queryKey: ["wikis", wikiSlug, "pages", pageSlug],
    }),
  ]);
  signal.throwIfAborted();
  const page = db.collection(wikiPageCollection(identity));
  const index = db.collection(wikiIndexCollection(identity));
  const revisions = db.collection(
    wikiRevisionsCollection({ ...identity, pageId: edit.page.id }),
  );
  await page.utils.writeUpsert(edit.page);

  if (index.status === "ready") await index.utils.writeUpsert(edit.page);

  if (revisions.status === "ready")
    await revisions.utils.writeUpsert(edit.revision);

  const refreshes: Promise<unknown>[] = [
    queryClient.invalidateQueries({
      queryKey: [
        "wikis",
        wikiSlug,
        "index",
        { elevatedPrivileges: !elevatedPrivileges },
      ],
    }),
    queryClient.invalidateQueries({
      queryKey: [
        "wikis",
        wikiSlug,
        "pages",
        pageSlug,
        { elevatedPrivileges: !elevatedPrivileges },
      ],
    }),
    queryClient.invalidateQueries({
      queryKey: ["wikis", wikiSlug, "pages", pageSlug, "revisions"],
    }),
  ];

  if (index.status === "loading") refreshes.push(index.utils.refetch());

  await Promise.allSettled(refreshes);
}

export async function saveWikiRevision({
  db,
  request,
  ...identity
}: WikiIdentity & { db: DbClient; request: Schemas["WikiEditRequest"] }) {
  const { wikiSlug, pageSlug, elevatedPrivileges } = identity;
  const signal = db.requireDependency<AbortController>("lifetime").signal;
  const edit = await apiPut<WikiEditPublic>(
    `/v2/wikis/${encodeURIComponent(wikiSlug)}/pages/${pageSlug.split("/").map(encodeURIComponent).join("/")}`,
    request,
    { signal, elevatedPrivileges },
  ).catch((error: Error) => {
    db.requireDependency<(error: Error) => void>("reportAuthorizationError")(
      error,
    );
    throw error;
  });
  await publishWikiRevision({ db, edit, ...identity });
}

export async function revertWikiRevision({
  db,
  pageId,
  revisionId,
  request,
  ...identity
}: WikiIdentity & {
  db: DbClient;
  pageId: string;
  revisionId: string;
  request: Schemas["WikiRevertRequest"];
}) {
  const signal = db.requireDependency<AbortController>("lifetime").signal;
  const edit = await apiPost<WikiEditPublic>(
    `/v2/wiki-pages/${encodeURIComponent(pageId)}/revisions/${encodeURIComponent(revisionId)}/revert`,
    request,
    { signal, elevatedPrivileges: identity.elevatedPrivileges },
  ).catch((error: Error) => {
    db.requireDependency<(error: Error) => void>("reportAuthorizationError")(
      error,
    );
    throw error;
  });
  await publishWikiRevision({ db, edit, ...identity });
}

export async function deleteWikiPage({
  db,
  pageId,
  reason,
  ...identity
}: WikiIdentity & { db: DbClient; pageId: string; reason: string }) {
  await db
    .collection(wikiPageCollection(identity))
    .delete(pageId, { metadata: { reason }, optimistic: false })
    .when("settled");
}
